import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { DueDateValue } from "@smartsuite/types";
import * as yup from "yup";
import format from "date-fns/format";
import { enUS } from "date-fns/locale";
import { InputGroup } from "@smartsuite/react-ui/lib";
import {
  DueDateCalendar,
  DueDateCalendarDateValue,
} from "@smartsuite/react-ui/lib/components/widgets/DatePicker/DueDateCalendar";

import { FormFieldProps } from "../../../types";
import { useFormField } from "../../../../hooks/useFormField";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import { $t } from "../../../../utils/intl";
import { texts } from "../due-date.text";
import {
  combineDateAndTime,
  defaultDueDateValue,
  getDefaultValueState,
  getDueDateValueString,
  getFormattedInputDate,
  initDatesFromValue,
} from "../due-date.helpers";

import "../due-date.sass";
import { getNow, isDateValid, textToDateValue } from "@smartsuite/fields-logic/lib/utils/date";
import { maxYearForSelect, minYearForSelect } from "../../date/DateFieldControl/DateFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";

type CalendarOutputKeys = keyof Pick<DueDateCalendarDateValue, "from_date" | "to_date">;

const yearsRange = {
  from: minYearForSelect,
  to: maxYearForSelect(),
};

type DueDateFieldControlProps = FormFieldProps;

export const DueDateFieldControl: FC<DueDateFieldControlProps> = ({
  name,
  label,
  context,
  caption,
  required,
  formItem,
  readOnly,
}) => {
  const { preferences } = useFormContext();
  const timezone = preferences.timezone.value;
  const now = useMemo(() => getNow(timezone), [timezone]);
  const timeFormat = context.config.form_state.time_format ?? "24h";
  const formField = useFormField<DueDateValue>({
    name,
    validate: validateWithYup(
      yup.object().when({
        is: () => formItem.required,
        then: (s) =>
          s.shape({
            to_date: yup.object().shape({
              date: yup.string().required($t(validatorText.validationRequired)),
              include_time: yup.boolean(),
            }),
            from_date: yup.object().shape({
              date: yup.string(),
              include_time: yup.boolean(),
            }),
          }),
      })
    ),
  });
  const [timeValues, setTimeValues] = useState<DueDateCalendarDateValue>({
    from_date: "",
    to_date: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [value, setValue] = useState<DueDateValue>(getDefaultValueState(formField?.value));
  const [calendarDateValues, setCalendarDateValues] = useState<DueDateValue>({
    from_date: {
      date: "",
      include_time: false,
    },
    to_date: {
      date: "",
      include_time: false,
    },
  });

  const handleChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;
      setFromDate(start);
      setEndDate(end);

      const formattedStartDate = start ? format(start, "MMM dd, yyyy") : "";
      const formattedEndDate = end ? format(end, "MMM dd, yyyy") : "";

      if (start && !end) {
        setCalendarDateValues((prevValues) => ({
          from_date: {
            date: "",
            include_time: prevValues.from_date.include_time,
          },
          to_date: {
            date: formattedStartDate,
            include_time: prevValues.to_date.include_time,
          },
        }));
        return;
      }

      setCalendarDateValues((prevValues) => ({
        from_date: {
          date: formattedStartDate,
          include_time: prevValues.from_date.include_time,
        },
        to_date: {
          date: formattedEndDate,
          include_time: prevValues.to_date.include_time,
        },
      }));
    },
    [fromDate, endDate]
  );

  const handleDoneClick = useCallback(() => {
    const updatedValue = {
      from_date: {
        date: combineDateAndTime(
          calendarDateValues.from_date.date ?? "",
          timeValues.from_date,
          timeFormat,
          timezone
        ),
        include_time: !!timeValues.from_date,
      },
      to_date: {
        date: combineDateAndTime(
          calendarDateValues.to_date.date ?? "",
          timeValues.to_date,
          timeFormat,
          timezone
        ),
        include_time: !!timeValues.to_date,
      },
    };
    setValue(updatedValue);
    formField.onChange(updatedValue);
  }, [
    calendarDateValues.to_date.include_time,
    calendarDateValues.from_date.include_time,
    calendarDateValues.from_date.date,
    calendarDateValues.to_date.date,
    timeFormat,
    timezone,
    timeValues.from_date,
    timeValues.to_date,
  ]);

  const handleChangeTime = useCallback(
    (timeValue: Partial<DueDateCalendarDateValue>) => {
      const key = Object.keys(timeValue)[0] as CalendarOutputKeys;

      setTimeValues((prevValues) => ({ ...prevValues, ...timeValue }));
      if (timeValue[key]) {
        setCalendarDateValues((prevValues) => ({
          ...prevValues,
          [key]: {
            date: prevValues[key].date,
            include_time: true,
          },
        }));
      } else {
        setCalendarDateValues((prevValues) => ({
          ...prevValues,
          [key]: {
            date: prevValues[key].date,
            include_time: false,
          },
        }));
      }
    },
    [calendarDateValues]
  );

  const handleChangeDateValue = useCallback(
    (value: Partial<DueDateCalendarDateValue>) => {
      const key = Object.keys(value)[0] as CalendarOutputKeys;
      const dateString = value[key] as string;

      if (!dateString) {
        const [clearedStart, clearedEnd] = getClearedDates(fromDate, endDate, key);
        handleChange([clearedStart, clearedEnd]);
        return;
      }

      const parsedDateValue = textToDateValue(dateString, preferences);
      const newDate = parsedDateValue?.date && new Date(parsedDateValue.date);
      const isValidDate = newDate && isDateValid(newDate);

      if (key === "to_date" && isValidDate && !fromDate) {
        handleChange([newDate, null]);
        return;
      }

      const hasValues = fromDate && endDate;

      if (isValidDate && hasValues) {
        if (key === "from_date" && endDate) {
          handleChange([newDate, endDate]);
        }
        if (key === "to_date" && fromDate) {
          handleChange([fromDate, newDate]);
        }
      }
    },
    [fromDate, endDate]
  );

  const handleMountDate = useCallback(() => {
    const { fromDate, endDate, timeValues, calendarDateValues } = initDatesFromValue(
      value,
      timeFormat,
      timezone
    );

    setTimeValues(timeValues);
    setCalendarDateValues(calendarDateValues);
    if (endDate && !fromDate) {
      setFromDate(endDate);
      setEndDate(null);
      return;
    }

    setFromDate(fromDate);
    setEndDate(endDate);
  }, [value, timeFormat, timezone]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        formField.onFocus();
      } else if (formField.state === "active") {
        formField.onBlur();
      }
    },
    [formField.state]
  );

  const handleClearValue = useCallback(() => {
    setValue(defaultDueDateValue);
    setFromDate(null);
    setEndDate(null);
    setTimeValues({ from_date: "", to_date: "" });
    setCalendarDateValues(defaultDueDateValue);
    formField.onChange(defaultDueDateValue);
  }, []);

  useEffect(() => {
    handleMountDate();
  }, []);

  const { formattedFromValue, formattedEndValue } = useMemo(() => {
    const formattedFrom = value?.from_date.date
      ? getDueDateValueString(
          value.from_date.include_time,
          value.from_date.date,
          timeFormat,
          timezone
        )
      : "";
    const formattedEnd = value?.to_date.date
      ? getDueDateValueString(value.to_date.include_time, value.to_date.date, timeFormat, timezone)
      : "";

    return { formattedFromValue: formattedFrom, formattedEndValue: formattedEnd };
  }, [value, timeFormat, timezone]);

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      state={formField.state}
      caption={caption}
      required={required}
      helpText={formItem.params.helpText}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
    >
      <InputGroup className="due-date-field-control" state={formField.state}>
        <DueDateCalendar
          mode="record-edit-form"
          dateFormat={"MM/DD/YYYY"}
          controlClasses="due-date-field-control__value"
          showBadgeStatus={false}
          today={now}
          calendarDateValues={calendarDateValues}
          locale={enUS}
          fromDate={fromDate}
          endDate={endDate}
          fromDateString={formattedFromValue}
          endDateString={formattedEndValue}
          opened={false}
          texts={texts}
          yearsOptionEnd={yearsRange.to}
          yearsOptionStart={yearsRange.from}
          timeFormat={timeFormat}
          onChangeTime={handleChangeTime}
          onChange={handleChange}
          onDoneClick={handleDoneClick}
          onCancelClick={handleMountDate}
          timeValues={timeValues}
          getFormattedInputDate={getFormattedInputDate}
          onChangeDateValue={handleChangeDateValue}
          onClearValue={handleClearValue}
          onOpenChange={handleOpenChange}
          highlightDates={[]}
        />
      </InputGroup>
    </FormFieldControl>
  );
};

const getClearedDates = (
  fromDate: Date | null,
  endDate: Date | null,
  key: string
): [Date | null, Date | null] => {
  if (key === "from_date" && endDate) {
    return [endDate, null];
  }

  if (key === "to_date" && fromDate && !endDate) {
    return [null, null];
  }

  if (key === "to_date" && fromDate) {
    return [fromDate, null];
  }

  if (key === "to_date" && !fromDate) {
    return [null, null];
  }

  return [null, null];
};
